import { useEffect, useState } from 'react';
import haxios from '../lib/haxios';

// useApi — thin wrapper around haxios for read-oriented screen data.
//
// url:      absolute or APIR-relative endpoint. Pass null to skip.
// initial:  value returned before first fetch + whenever a fetch fails
// method:   'get' | 'post'
// data:     body / query
// refetch:  polling interval in ms (optional)
//
// Errors — the hook **intentionally swallows** non-network errors (404, 500,
// malformed JSON) and resolves with `initial`. This keeps screens responsive
// against un-wired endpoints (the rebuild references endpoints the backend
// hasn't shipped yet — see BACKEND_CHANGES.md). Callers get `isError` and
// `error` on the returned object if they need to differentiate.

const useApi = ({ url, initial = null, method = 'get', data: body = {}, refetch = null }) => {
  const [isLoading, setIsLoading]   = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const [data, setData]             = useState(initial);
  const [error, setError]           = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = async () => {
    if (!url) {
      setIsFetching(false);
      setIsLoading(false);
      return;
    }
    setIsFetching(true);
    setError(null);
    try {
      let res;
      if (method.toLowerCase() === 'get') {
        res = await haxios.get(url, { params: body });
      } else if (method.toLowerCase() === 'post') {
        res = await haxios.post(url, body);
      }
      setData(res);
      setLastUpdated(new Date());
    } catch (err) {
      const status = err?.response?.status;
      // 404 / 5xx / offline — leave the screen with `initial` and surface
      // an error marker. Log quietly so it shows up in devtools but doesn't
      // trip CRA's runtime overlay.
      console.warn(`[useApi] ${url} failed (${status || 'network'})`);
      setError({ status: status || null, message: err?.message || 'request failed' });
      setData(initial);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(body), url]);

  useEffect(() => {
    if (!refetch) return;
    const interval = setInterval(() => loadData(), refetch);
    return () => clearInterval(interval);
    // Reset the interval when the target changes, so background polling
    // never refetches a stale url/body (e.g. after the operator switches).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, url, JSON.stringify(body)]);

  return {
    isLoading,
    isFetching,
    data,
    error,
    isError: !!error,
    lastUpdated,
    refetch: loadData,
  };
};

export default useApi;
